import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HomeIcon, 
  ClockIcon,
  DocumentTextIcon,
  Bars3Icon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAdmin } from '../contexts/PostureContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import PostureXeLogo from './PostureXeLogo.tsx';
import { LanguageSwitcher } from './LanguageSwitcher.tsx';

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = useAdmin();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = useMemo(
    () => [
      { name: t('nav.dashboard'), href: '/', icon: HomeIcon },
      { name: t('nav.sessions'), href: '/history', icon: ClockIcon },
      { name: t('nav.devices'), href: '/reports', icon: DocumentTextIcon },
    ],
    [t]
  );

  return (
    <div className="flex h-screen bg-regina-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col bg-gradient-to-b from-white to-regina-gray-50 border-r border-regina-gray-200 shadow-lg
      `}>
        {/* Logo Section */}
        <div className="flex items-center justify-between min-h-[5.5rem] py-3 px-5 bg-white shrink-0 overflow-hidden gap-3">
          <PostureXeLogo />
          <div className="flex shrink-0 items-center gap-2">
            <LanguageSwitcher />
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg text-regina-gray-600 hover:bg-regina-gray-100 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          </div>
        </div>

        {/* System Status */}
        {state.systemStats && (
          <div className="px-6 py-5">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-regina-gray-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-regina-gray-700 uppercase tracking-wide">{t('layout.systemStatus')}</span>
                <div className={`w-2.5 h-2.5 rounded-full ${
                  state.systemStats.online_devices > 0 ? 'bg-regina-success animate-pulse shadow-lg shadow-green-400/50' : 'bg-regina-gray-400'
                }`} />
              </div>
              <div className="text-base text-regina-gray-900 font-bold">
                {state.systemStats.online_devices}/{state.systemStats.total_devices}
              </div>
              <div className="text-xs text-regina-gray-600 mt-1">
                {t('layout.onlineDevices')}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-4 py-4 space-y-2">
          <div className="text-xs font-bold text-regina-gray-500 uppercase tracking-wider px-4 mb-3">
            {t('layout.mainMenu')}
          </div>
          {navigation.map((item) => {
            // Check if current route is active
            // For "Sesiuni" (/history), also match /session/* routes (session details)
            const isActive = location.pathname === item.href || 
                            (item.href === '/history' && location.pathname.startsWith('/session'));
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`
                  group flex items-center px-4 py-3.5 text-sm font-semibold rounded-xl transition-all duration-200
                  ${isActive
                    ? 'bg-gradient-to-r from-regina-red to-red-600 text-white shadow-lg shadow-regina-red/30 scale-105'
                    : 'text-regina-gray-700 hover:bg-white hover:text-regina-red hover:shadow-md hover:scale-102'
                  }
                `}
              >
                <item.icon className={`w-5 h-5 mr-4 transition-transform ${
                  isActive ? 'text-white scale-110' : 'text-regina-gray-500 group-hover:text-regina-red group-hover:scale-110'
                }`} />
                <span className="tracking-wide">{item.name}</span>
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-regina-gray-200 space-y-2 shrink-0">
          <div className="px-4 text-xs text-regina-gray-500 truncate" title={user?.username}>
            {user?.username}
          </div>
          <button
            type="button"
            onClick={() => {
              logout();
              window.location.assign('/login');
            }}
            className="w-full flex items-center px-4 py-3 text-sm font-semibold rounded-xl text-regina-gray-700 hover:bg-white hover:text-regina-red hover:shadow-md transition-all"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3 text-regina-gray-500" />
            {t('layout.logout')}
          </button>
        </div>

      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile top bar: menu lives in document flow so it never overlaps page titles */}
        <header className="lg:hidden shrink-0 sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-regina-gray-200 bg-white/95 px-3 shadow-sm backdrop-blur-sm">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('layout.openMenu')}
            className="shrink-0 rounded-lg p-2 text-regina-gray-700 transition-colors hover:bg-regina-gray-100"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-regina-gray-800">
            {t('layout.mobileHeaderTitle')}
          </span>
          <LanguageSwitcher className="shrink-0" />
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto bg-regina-gray-50">
          <div className="mx-auto max-w-[1600px] px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default Layout;
